"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import RashifalAudioPlayer from "@/components/RashifalAudioPlayer";

export interface DailyRashiData {
  id: string;
  name: string;
  hindi: string;
  ruler: string;
  element: "Fire" | "Earth" | "Air" | "Water";
  symbol: string;
  moonHouse: number;
  focusArea: string;
  hindiFocus: string;
  luckyColor: string;
  luckyNumber: number;
  shubhTiming: string;
  upay: string;
  hindiUpay: string;
  forecast: string;
  hindiForecast: string;
}

export const DAILY_RASHIS: DailyRashiData[] = [
  {
    id: "mesha",
    name: "Aries (Mesha)",
    hindi: "मेष",
    ruler: "Mars (Mangal)",
    element: "Fire",
    symbol: "♈",
    moonHouse: 1,
    focusArea: "Vitality, Leadership & New Initiatives",
    hindiFocus: "आत्मविश्वास, ऊर्जा व नए कार्य की शुरुआत",
    luckyColor: "Deep Red / Crimson",
    luckyNumber: 9,
    shubhTiming: "11:45 AM – 12:35 PM (Abhijit)",
    upay: "Chant 'Om Kram Kreem Kroum Sah Bhaumaya Namah' 11 times.",
    hindiUpay: "हनुमान चालीसा का पाठ करें और तांबे के पात्र से जल पिएं।",
    forecast:
      "High planetary energy supports decisive career moves. Avoid impulsive financial decisions before midday.",
    hindiForecast:
      "आज साहस और ऊर्जा बनी रहेगी। कार्यक्षेत्र में नए अवसर मिलेंगे, बस क्रोध पर नियंत्रण रखें।",
  },
  {
    id: "vrishabha",
    name: "Taurus (Vrishabha)",
    hindi: "वृषभ",
    ruler: "Venus (Shukra)",
    element: "Earth",
    symbol: "♉",
    moonHouse: 12,
    focusArea: "Financial Budgeting & Long-Term Planning",
    hindiFocus: "आर्थिक नियोजन, व्यय नियंत्रण व शांति",
    luckyColor: "Pure White / Cream",
    luckyNumber: 6,
    shubhTiming: "02:15 PM – 03:45 PM (Amrit)",
    upay: "Offer white flowers or raw milk to Shiva Lingam in the morning.",
    hindiUpay: "शिवलिंग पर कच्चा दूध अर्पित करें और सफेद वस्त्र पहनें।",
    forecast:
      "Steady progress in commerce. Ensure proper sleep and avoid speculative short-term expenses.",
    hindiForecast:
      "व्यापार में धैर्य से लाभ होगा। गैर-जरूरी खर्चों से बचें और मानसिक शांति बनाए रखें।",
  },
  {
    id: "mithuna",
    name: "Gemini (Mithuna)",
    hindi: "मिथुन",
    ruler: "Mercury (Budh)",
    element: "Air",
    symbol: "♊",
    moonHouse: 11,
    focusArea: "Profitable Networking & Social Alliances",
    hindiFocus: "लाभ, नए संपर्क व मित्रों का सहयोग",
    luckyColor: "Emerald Green",
    luckyNumber: 5,
    shubhTiming: "09:30 AM – 11:00 AM (Shubh)",
    upay: "Feed green grass or spinach to a cow or water a Tulsi plant.",
    hindiUpay: "तुलसी में जल दें और गणेश जी को दूर्वा अर्पित करें।",
    forecast:
      "Excellent transit for closing deals, communication breakthroughs, and financial rewards.",
    hindiForecast: "संचार और व्यापारिक बातचीत में सफलता मिलेगी। मित्रों से धन लाभ का योग है।",
  },
  {
    id: "karka",
    name: "Cancer (Karka)",
    hindi: "कर्क",
    ruler: "Moon (Chandra)",
    element: "Water",
    symbol: "♋",
    moonHouse: 10,
    focusArea: "Professional Standing & Career Recognition",
    hindiFocus: "कार्यक्षेत्र में प्रतिष्ठा व पदोन्नति",
    luckyColor: "Silver / Pearl White",
    luckyNumber: 2,
    shubhTiming: "10:15 AM – 11:45 AM (Labha)",
    upay: "Drink water from a silver vessel and seek mother's blessings.",
    hindiUpay: "माताजी के चरण स्पर्श करें और ॐ सोम सोमाय नमः का जप करें।",
    forecast:
      "Authorities recognize your dedication. Maintain emotional balance during afternoon team meetings.",
    hindiForecast: "वरिष्ठ अधिकारियों का सहयोग मिलेगा। पारिवारिक मामलों में धैर्य से काम लें।",
  },
  {
    id: "simha",
    name: "Leo (Simha)",
    hindi: "सिंह",
    ruler: "Sun (Surya)",
    element: "Fire",
    symbol: "♌",
    moonHouse: 9,
    focusArea: "Spiritual Elevation, Dharma & Fortunate Travel",
    hindiFocus: "भाग्य वृद्धि, आध्यात्मिक कार्य व यात्रा",
    luckyColor: "Gold / Bright Orange",
    luckyNumber: 1,
    shubhTiming: "08:00 AM – 09:30 AM (Shubh)",
    upay: "Offer Arghya (water mixed with red sandalwood) to the rising Sun.",
    hindiUpay: "प्रातः तांबे के लोटे से सूर्य को जल दें और गायत्री मंत्र पढ़ें।",
    forecast:
      "Strong Bhagya transit removes long-standing hurdles in higher education and legal matters.",
    hindiForecast: "भाग्य का साथ मिलेगा। रुके हुए सरकारी काम पूरे होने के अच्छे संकेत हैं।",
  },
  {
    id: "kanya",
    name: "Virgo (Kanya)",
    hindi: "कन्या",
    ruler: "Mercury (Budh)",
    element: "Earth",
    symbol: "♍",
    moonHouse: 8,
    focusArea: "Analytical Research & Health Maintenance",
    hindiFocus: "गहन अध्ययन, स्वास्थ्य व सतर्कता",
    luckyColor: "Sage Green / Pistachio",
    luckyNumber: 5,
    shubhTiming: "03:30 PM – 05:00 PM (Amrit)",
    upay: "Chant 'Om Budhaya Namah' and donate green pulses (Moong Dal).",
    hindiUpay: "हरे मूंग का दान करें और छोटी कन्याओं का आशीर्वाद लें।",
    forecast:
      "Favorable for deep analytical work, audits, and resolving past tax or insurance filings.",
    hindiForecast: "रिसर्च और तकनीकी काम में मन लगेगा। खान-पान में सात्विकता बनाए रखें।",
  },
  {
    id: "tula",
    name: "Libra (Tula)",
    hindi: "तुला",
    ruler: "Venus (Shukra)",
    element: "Air",
    symbol: "♎",
    moonHouse: 7,
    focusArea: "Marital Harmony & Business Partnerships",
    hindiFocus: "दांपत्य सुख व साझेदारी व्यापार",
    luckyColor: "Pastel Blue / Soft Pink",
    luckyNumber: 6,
    shubhTiming: "12:00 PM – 01:30 PM (Labha)",
    upay: "Apply sandalwood paste on forehead and maintain pleasant demeanor.",
    hindiUpay: "इत्र या चंदन का प्रयोग करें और जीवनसाथी को उपहार दें।",
    forecast:
      "Harmony prevails in relationships. Ideal time for signing mutual agreements and joint ventures.",
    hindiForecast: "साझेदारी और वैवाहिक जीवन में मधुरता रहेगी। नए व्यापारिक अनुबंध संभव हैं।",
  },
  {
    id: "vrishchika",
    name: "Scorpio (Vrishchika)",
    hindi: "वृश्चिक",
    ruler: "Mars (Mangal)",
    element: "Water",
    symbol: "♏",
    moonHouse: 6,
    focusArea: "Overcoming Rivals & Daily Discipline",
    hindiFocus: "शत्रु विजय, कर्ज मुक्ति व कार्य निष्पादन",
    luckyColor: "Maroon / Coral Red",
    luckyNumber: 9,
    shubhTiming: "04:30 PM – 06:00 PM (Shubh)",
    upay: "Recite the 4th chapter of Hanuman Chalisa or visit a Hanuman temple.",
    hindiUpay: "हनुमान जी को सिंदूर व चमेली का तेल अर्पित करें।",
    forecast:
      "Victory in competitive exams and negotiations. Physical workout brings great stamina.",
    hindiForecast: "प्रतिस्पर्धा में जीत मिलेगी। पुराने विवाद सुलझने का अनुकूल समय है।",
  },
  {
    id: "dhanu",
    name: "Sagittarius (Dhanu)",
    hindi: "धनु",
    ruler: "Jupiter (Guru)",
    element: "Fire",
    symbol: "♐",
    moonHouse: 5,
    focusArea: "Creative Intellect, Investments & Children",
    hindiFocus: "बुद्धि, रचनात्मकता, संतान व निवेश",
    luckyColor: "Bright Yellow / Saffron",
    luckyNumber: 3,
    shubhTiming: "09:00 AM – 10:30 AM (Amrit)",
    upay: "Apply saffron (Kesar) tilak and respect teachers/gurus.",
    hindiUpay: "केसर का तिलक लगाएं और गुरुजनों का आशीर्वाद लें।",
    forecast:
      "High creative clarity. Thoughtful financial investments yield solid long-term returns.",
    hindiForecast:
      "विद्यार्थियों के लिए उत्तम दिन है। रचनात्मक कार्यों में विशेष सम्मान प्राप्त होगा।",
  },
  {
    id: "makara",
    name: "Capricorn (Makara)",
    hindi: "मकर",
    ruler: "Saturn (Shani)",
    element: "Earth",
    symbol: "♑",
    moonHouse: 4,
    focusArea: "Property, Vehicles & Domestic Contentment",
    hindiFocus: "गृह सुख, माता का आशीर्वाद व संपत्ति",
    luckyColor: "Charcoal / Royal Blue",
    luckyNumber: 8,
    shubhTiming: "01:30 PM – 03:00 PM (Shubh)",
    upay: "Light a mustard oil lamp under a Peepal tree in the evening.",
    hindiUpay: "शाम को पीपल के नीचे सरसों के तेल का दीपक जलाएं।",
    forecast:
      "Favorable for purchasing home furnishings, vehicles, or discussing real estate acquisitions.",
    hindiForecast: "भूमि, मकान या वाहन के क्रय-विक्रय के लिए शुभ समय है। घर में शांति रहेगी।",
  },
  {
    id: "kumbha",
    name: "Aquarius (Kumbha)",
    hindi: "कुंभ",
    ruler: "Saturn (Shani)",
    element: "Air",
    symbol: "♒",
    moonHouse: 3,
    focusArea: "Courage, Short Journeys & Sibling Bonds",
    hindiFocus: "पराक्रम, छोटी यात्राएँ व भाई-बहनों का साथ",
    luckyColor: "Navy Blue / Sky Blue",
    luckyNumber: 8,
    shubhTiming: "10:30 AM – 12:00 PM (Labha)",
    upay: "Help a needy person or donate dark blue garments.",
    hindiUpay: "जरूरतमंदों की सहायता करें और शनि चालीसा का पाठ करें।",
    forecast:
      "Initiatives taken today bear immediate fruit. Short business trips are highly productive.",
    hindiForecast: "आत्मविश्वास में वृद्धि होगी। नई योजनाओं पर काम शुरू करने का सही दिन है।",
  },
  {
    id: "meena",
    name: "Pisces (Meena)",
    hindi: "मीन",
    ruler: "Jupiter (Guru)",
    element: "Water",
    symbol: "♓",
    moonHouse: 2,
    focusArea: "Wealth Accumulation & Melodious Speech",
    hindiFocus: "धन संचय, वाणी प्रभाव व पारिवारिक सुख",
    luckyColor: "Golden Yellow / Cream",
    luckyNumber: 3,
    shubhTiming: "08:30 AM – 10:00 AM (Shubh)",
    upay: "Feed yellow gram (Chana Dal) and jaggery to a cow.",
    hindiUpay: "गाय को चना दाल व गुड़ खिलाएं और ॐ गुरवे नमः का जप करें।",
    forecast:
      "Financial inflows improve. Your spoken words resolve delicate family conversations gracefully.",
    hindiForecast: "आर्थिक स्थिति मजबूत होगी। परिवार में किसी मांगलिक कार्य की रूपरेखा बनेगी।",
  },
];

export default function DailyGocharRashifalStudio({ isHindi = false }: { isHindi?: boolean }) {
  const [selectedRashiId, setSelectedRashiId] = useState("mesha");

  const rashi = useMemo(
    () => DAILY_RASHIS.find((r) => r.id === selectedRashiId) || DAILY_RASHIS[0],
    [selectedRashiId]
  );

  const handleConsult = () => {
    safeSessionSet(
      "ishvaram_intent_context",
      JSON.stringify({
        source: isHindi ? "/hi/aaj-ka-rashifal" : "/rashifal",
        rashi: rashi.name,
        ruler: rashi.ruler,
      })
    );
  };

  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/rashifal"),
    "/rashifal",
    isHindi ? "hi" : "en"
  );
  const shareText = encodeURIComponent(
    `Today's Vedic Rashifal for ${rashi.name}: ${rashi.focusArea} (Lucky Color: ${rashi.luckyColor}) on Ishvaram. https://ishvaram.com/rashifal/`
  );
  const whatsappShareUrl = `https://api.whatsapp.com/send?text=${shareText}`;

  return (
    <div className="my-8 rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-7 shadow-xs">
      <StudioHeader isHindi={isHindi} whatsappShareUrl={whatsappShareUrl} />
      <RashiSelectorGrid
        selectedId={selectedRashiId}
        onSelect={setSelectedRashiId}
        isHindi={isHindi}
      />
      <RashiTransitCard
        rashi={rashi}
        isHindi={isHindi}
        chatHref={chatHref}
        onConsult={handleConsult}
      />
    </div>
  );
}

function StudioHeader({
  isHindi,
  whatsappShareUrl,
}: {
  isHindi: boolean;
  whatsappShareUrl: string;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-border-default">
      <div>
        <span className="text-caption font-bold uppercase tracking-wider text-intent-brand">
          {isHindi ? "दैनिक गोचर आधारित राशिफल" : "Daily Planetary Gochar Engine"}
        </span>
        <h2 className="text-2xl font-bold font-serif text-text-primary mt-1">
          {isHindi
            ? "आज का वैदिक चंद्र राशिफल व गोचर विश्लेषण"
            : "Today's Vedic Moon-Sign Rashifal & Transit Studio"}
        </h2>
      </div>

      <a
        href={whatsappShareUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-lg border border-border-default bg-surface-raised px-3.5 py-2 text-caption font-bold text-text-primary hover:bg-surface-warm transition-colors shrink-0"
      >
        <span>📱</span>
        <span>{isHindi ? "WhatsApp पर शेयर करें" : "Share on WhatsApp"}</span>
      </a>
    </div>
  );
}

function RashiSelectorGrid({
  selectedId,
  onSelect,
  isHindi,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
  isHindi: boolean;
}) {
  return (
    <div className="my-6 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
      {DAILY_RASHIS.map((item) => {
        const isSelected = item.id === selectedId;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            className={`p-2.5 rounded-xl border text-center transition-all ${
              isSelected
                ? "border-intent-brand bg-surface-brand-subtle shadow-xs ring-2 ring-intent-brand/20 text-text-primary"
                : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
            }`}
          >
            <span className="text-lg block mb-0.5">{item.symbol}</span>
            <span className="text-body-small font-bold block truncate">
              {isHindi ? item.hindi : item.name.split(" ")[0]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function RashiTransitCard({
  rashi,
  isHindi,
  chatHref,
  onConsult,
}: {
  rashi: DailyRashiData;
  isHindi: boolean;
  chatHref: string;
  onConsult: () => void;
}) {
  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border-default">
        <div className="flex items-center gap-3">
          <span className="text-3xl">{rashi.symbol}</span>
          <div>
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {isHindi ? `${rashi.hindi} राशि — आज का गोचर फल` : `${rashi.name} — Today's Transit`}
            </h3>
            <p className="text-caption text-text-secondary mt-0.5">
              {isHindi
                ? `स्वामी: ${rashi.ruler} | तत्व: ${rashi.element}`
                : `Ruler: ${rashi.ruler} | Element: ${rashi.element}`}
            </p>
          </div>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          {isHindi ? "व्यक्तिगत कुंडली से पूछें ✦" : "Analyze My Chart with AI ✦"}
        </a>
      </div>

      <div className="my-5 p-4 rounded-xl bg-surface-raised border border-border-default">
        <p className="text-body-small leading-relaxed text-text-primary font-medium">
          {isHindi ? rashi.hindiForecast : rashi.forecast}
        </p>
        <div className="mt-3 pt-3 border-t border-border-default">
          <RashifalAudioPlayer
            textToSpeak={isHindi ? rashi.hindiForecast : rashi.forecast}
            rashiName={isHindi ? rashi.hindi : rashi.name.split(" ")[0]}
            isHindi={isHindi}
          />
        </div>
      </div>

      <TransitMetricsGrid rashi={rashi} isHindi={isHindi} />

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          {isHindi
            ? "यह गोचर चंद्र राशि पर आधारित है। अपनी सटीक कुंडली के लिए मुफ्त जन्म कुंडली बनाएं।"
            : "Calculated from current Swiss Ephemeris Moon transits over your birth Chandra Rashi."}
        </span>
        <Link
          href="/free-kundali/"
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1 shrink-0"
        >
          {isHindi ? "मुफ्त जन्म कुंडली बनाएँ →" : "Calculate Full Birth Chart →"}
        </Link>
      </div>
    </div>
  );
}

function TransitMetricsGrid({ rashi, isHindi }: { rashi: DailyRashiData; isHindi: boolean }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <div className="p-3 rounded-lg border border-border-default bg-surface-raised">
        <span className="text-caption font-bold uppercase tracking-wider text-text-muted block mb-1">
          {isHindi ? "शुभ रंग" : "Lucky Color"}
        </span>
        <span className="text-body-small font-bold text-text-primary">{rashi.luckyColor}</span>
      </div>

      <div className="p-3 rounded-lg border border-border-default bg-surface-raised">
        <span className="text-caption font-bold uppercase tracking-wider text-text-muted block mb-1">
          {isHindi ? "शुभ अंक" : "Lucky Number"}
        </span>
        <span className="text-body-small font-bold text-intent-brand">{rashi.luckyNumber}</span>
      </div>

      <div className="p-3 rounded-lg border border-border-default bg-surface-raised">
        <span className="text-caption font-bold uppercase tracking-wider text-text-muted block mb-1">
          {isHindi ? "शुभ समय" : "Shubh Timing"}
        </span>
        <span className="text-caption font-semibold text-text-primary">{rashi.shubhTiming}</span>
      </div>

      <div className="p-3 rounded-lg border border-border-default bg-surface-raised">
        <span className="text-caption font-bold uppercase tracking-wider text-text-muted block mb-1">
          {isHindi ? "दैनिक उपाय" : "Daily Upay"}
        </span>
        <span className="text-caption text-text-secondary">
          {isHindi ? rashi.hindiUpay : rashi.upay}
        </span>
      </div>
    </div>
  );
}
